/**
 * FirmaKartlarPage.jsx — Firma Kartları (Firmalar modülünün giriş ekranı)
 * Route: /Management/Firmalar/Kartlar  (?tab=tum → "Tüm Firmalar" sekmesi açık gelir)
 *
 * Servis: services/firmaFuarKartService.js → GET /firma/fuar-kartlari
 * Rehber: docs/guides/FE-FirmaController-guide.md §1
 *
 * Tek kart, iki sekme (2026-09-15'e kadar üst üste iki bölümdü):
 *
 *   1. "Fuara Eşlenmiş Firmalar" — seçili fuara `firma_to_fuar` üzerinden bağlı
 *      firmalar. Her kart, firmanın O FUARDAKİ rolünü (Katılımcı / Dekoratör /
 *      Organizatör ve üçlünün dışındakiler) rozet olarak taşır. Bu bölümün tamamı
 *      2026-09-12'ye kadar ayrı bir "Fuar Firmaları" sayfasıydı.
 *   2. "Tüm Firmalar" — veritabanındaki bütün firma kütüğü (28.970 satır), fuardan
 *      bağımsız, aranabilir ve sayfalı. Rol/stand alanları bu modda uç tarafından
 *      zaten boş döner, kart da onları göstermez.
 *
 * İki sekmenin de kendi arama kutusu ve durumu var (#551). Fuar sekmesinde uç aramayı
 * firma id / ad / kısa ad / salon adı / stand adları üzerinde yapar; "Tüm Firmalar"da
 * yalnızca id / ad / kısa ad. Arama durumu sayfada tutulduğu için sekme değişince kalır.
 *
 * İki sorgu da sekmeden bağımsız çalışır — sekme başlıklarındaki sayaçlar ikisini
 * birden gösterdiği için pasif sekmenin toplamı da gerekli.
 *
 * Satırlar Standlar / Kişi Listesi / Kullanıcılar listelerinin satır diliyle (FirmaKart.jsx, #593); araç çubuğu kendi kartı,
 * arama yazdıkça çalışır, sayfa düğmeleri satırların dışında.
 *
 * Bir satırın logosu / adı / "Detaylar" düğmesi Firma Detay sayfasına (FirmaDetayPage.jsx) götürür; profil
 * belgeleri, personel belgeleri ve taşeron bağlantıları orada birer sekme. Aktif
 * sekme URL'de tutulur ki Detay'dan geri dönüşte aynı sekme açılsın.
 *
 * 2026-09-12 birleştirmesi: "Eşlenmiş Firmalar" (FirmaListPage, mock) ve "Fuar
 * Firmaları" (FirmaFuarKartListPage) menü girdileri kaldırıldı — üçü de aynı soruyu
 * soruyordu ve ikisi mock veriyle cevaplıyordu. Kalan tek giriş burası, tamamı canlı.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardHeader, CardBody, Button, Input, InputGroup, InputGroupText, Row, Col, Spinner, Alert,
  Nav, NavItem, NavLink, Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { Plus, RefreshCw, Search } from "lucide-react";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import MainContext from "../../../../Components/Contexts/MainContext";
import FirmaKart from "../../../../Components/Firma/FirmaKart";
import CreateFirmaModal from "../../../../Components/Firma/CreateFirmaModal";
import { getFuarFirmaKartlari } from "../../../../services/firmaFuarKartService";
import { DEFAULT_FUAR_ID } from "../../../../services/personnelAdminService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../../i18n";

const FUAR_PAGE_SIZE = 10;
const GLOBAL_PAGE_SIZE = 10;

const TAB_FUAR = "fuar";
const TAB_TUM = "tum";

/** Boş/hata/yükleniyor durumlarını tek yerde toplayan ince sarmalayıcı. */
function SectionState({ loading, error, empty, emptyText }) {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" size="sm" />
      </div>
    );
  }
  if (error) return <Alert color="danger" className="mb-0">{error}</Alert>;
  if (empty) {
    return (
      <Card className="border border-dashed shadow-none mb-0">
        <CardBody className="text-center py-4 text-muted fs-13">{emptyText}</CardBody>
      </Card>
    );
  }
  return null;
}

/**
 * Simgeli arama kutusu + (arama açıksa) Temizle — iki sekmede aynı, durumları ayrı. Standlar / Kullanıcılar araç çubuğunun
 * dili (#593); yazdıkça arar (bkz. useYazdikcaAra).
 * WAS (#593 öncesi): Ara düğmeli bir form (sadece "Ara"ya basınca arardı).
 */
function SearchBar({ placeholder, input, onInput, active, onClear }) {
  const { t } = useTranslation("moduller");
  return (
    <>
      <Col lg={4} md={6}>
        <InputGroup>
          <InputGroupText><Search size={14} /></InputGroupText>
          <Input placeholder={placeholder} value={input} onChange={(e) => onInput(e.target.value)} />
        </InputGroup>
      </Col>
      {active && (
        <Col md="auto">
          <Button color="link" size="sm" className="p-0 text-decoration-none" onClick={onClear}>{t("firmaKartlar.temizle")}</Button>
        </Col>
      )}
    </>
  );
}

/** Yazdıkça arar: giriş durunca (350 ms) uygulanır ve liste 1. sayfaya döner. `uygula` / `ilkSayfa` state setter'ları. */
function useYazdikcaAra(girdi, uygulanan, uygula, ilkSayfa) {
  useEffect(() => {
    const yeni = girdi.trim();
    if (yeni === uygulanan) return undefined;
    const h = setTimeout(() => { uygula(yeni); ilkSayfa(1); }, 350);
    return () => clearTimeout(h);
  }, [girdi, uygulanan, uygula, ilkSayfa]);
}

/** Sekme başlığı — ikon + ad + soft badge sayaç. */
function TabLink({ active, onClick, icon, color, label, count }) {
  return (
    <NavItem>
      <NavLink active={active} onClick={onClick} style={{ cursor: "pointer" }} className="py-3">
        <i className={`bx ${icon} me-1 align-middle`} />
        {label}
        {count !== null && count !== undefined && (
          <span className={`badge bg-${color}-subtle text-${color} ms-2 align-middle`}>
            {count.toLocaleString(currentLocaleTag())}
          </span>
        )}
      </NavLink>
    </NavItem>
  );
}

export default function FirmaKartlarPage() {
  const { t: tx } = useTranslation("moduller");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === TAB_TUM ? TAB_TUM : TAB_FUAR;
  const setActiveTab = (tab) => setSearchParams(tab === TAB_TUM ? { tab: TAB_TUM } : {}, { replace: true });

  const { selectedFuarId } = useContext(MainContext);
  const fuarId = Number(selectedFuarId) || DEFAULT_FUAR_ID;

  // ─── Sekme 1: fuara eşlenmiş firmalar ───────────────────────────────────────
  const [fuarRows, setFuarRows] = useState([]);
  const [fuarTotal, setFuarTotal] = useState(0);
  const [fuarPage, setFuarPage] = useState(1);
  const [fuarLoading, setFuarLoading] = useState(true);
  const [fuarError, setFuarError] = useState(null);
  const [fuarSearchInput, setFuarSearchInput] = useState("");
  const [fuarSearch, setFuarSearch] = useState("");

  // ─── Sekme 2: tüm firma kütüğü ──────────────────────────────────────────────
  const [globalRows, setGlobalRows] = useState([]);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [globalPage, setGlobalPage] = useState(1);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => { document.title = i18n.t("moduller:firmaKartlar.firmaKartlariFds"); }, []);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setFuarLoading(true);
      setFuarError(null);
      try {
        const data = await getFuarFirmaKartlari({ fuarId, search: fuarSearch, page: fuarPage, limit: FUAR_PAGE_SIZE });
        if (!ignore) { setFuarRows(data.records); setFuarTotal(data.total); }
      } catch (e) {
        if (!ignore) setFuarError(e?.response?.data?.message || e.message || i18n.t("moduller:firmaKartlar.fuarFirmalariOkunamadi"));
      } finally {
        if (!ignore) setFuarLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarId, fuarSearch, fuarPage, reloadToken]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setGlobalLoading(true);
      setGlobalError(null);
      try {
        const data = await getFuarFirmaKartlari({ allFirms: true, search, page: globalPage, limit: GLOBAL_PAGE_SIZE });
        if (!ignore) { setGlobalRows(data.records); setGlobalTotal(data.total); }
      } catch (e) {
        if (!ignore) setGlobalError(e?.response?.data?.message || e.message || i18n.t("moduller:firmaKartlar.firmaKutuguOkunamadi"));
      } finally {
        if (!ignore) setGlobalLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [search, globalPage, reloadToken]);

  const firmaUrl = useCallback((firma) => `/Management/Firmalar/Detay/${firma.firmaId}?fuarId=${fuarId}`, [fuarId]);
  const openFirma = useCallback((firma) => navigate(firmaUrl(firma)), [navigate, firmaUrl]);

  useYazdikcaAra(searchInput, search, setSearch, setGlobalPage);
  useYazdikcaAra(fuarSearchInput, fuarSearch, setFuarSearch, setFuarPage);

  const clearSearch = () => { setSearchInput(""); setSearch(""); setGlobalPage(1); };

  const clearFuarSearch = () => { setFuarSearchInput(""); setFuarSearch(""); setFuarPage(1); };

  const fuarPageCount = Math.max(1, Math.ceil(fuarTotal / FUAR_PAGE_SIZE));
  const globalPageCount = Math.max(1, Math.ceil(globalTotal / GLOBAL_PAGE_SIZE));

  // Eşlenmiş firmaların rol dağılımı (bu sayfadakiler) — sekme araç çubuğunda.
  const rolOzeti = useMemo(() => {
    const counts = {};
    fuarRows.forEach((f) => f.roles.forEach((r) => { counts[r.name] = (counts[r.name] || 0) + 1; }));
    const parts = Object.entries(counts).map(([name, n]) => `${n} ${name}`);
    return parts.length ? parts.join(" · ") : null;
  }, [fuarRows]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("firmaKartlar.firmaKartlari")} pageTitle={tx("firmaKartlar.firmalar")} />

        {/* Araç çubuğu kendi kartı (Standlar / Kullanıcılar gibi, #593); satırlar kartın dışında, her biri kendi kartı.
            WAS: sekmeler + arama + satırlar tek büyük Card'ın içindeydi, Yenile / Yeni Firma sekme başlığındaydı. */}
        <Card className="mb-3">
          <CardHeader className="border-bottom-0 pb-0">
            <Nav tabs className="nav-tabs-custom card-header-tabs border-bottom-0">
              <TabLink
                active={activeTab === TAB_FUAR}
                onClick={() => setActiveTab(TAB_FUAR)}
                icon="bx-link"
                color="primary"
                label={tx("firmaKartlar.fuaraEslenmisFirmalar")}
                count={fuarLoading ? null : fuarTotal}
              />
              <TabLink
                active={activeTab === TAB_TUM}
                onClick={() => setActiveTab(TAB_TUM)}
                icon="bx-buildings"
                color="info"
                label={tx("firmaKartlar.tumFirmalar")}
                count={globalLoading ? null : globalTotal}
              />
            </Nav>
          </CardHeader>

          <CardBody className="border-top">
            <Row className="align-items-center g-2">
              {activeTab === TAB_FUAR ? (
                <SearchBar
                  placeholder={tx("firmaKartlar.firmaAdiKisaAdSalonStand")}
                  input={fuarSearchInput}
                  onInput={setFuarSearchInput}
                  active={Boolean(fuarSearch)}
                  onClear={clearFuarSearch}
                />
              ) : (
                <SearchBar
                  placeholder={tx("firmaKartlar.firmaAdiKisaAdVeya")}
                  input={searchInput}
                  onInput={setSearchInput}
                  active={Boolean(search)}
                  onClear={clearSearch}
                />
              )}
              <Col md="auto" className="ms-auto d-flex gap-2">
                <Button color="secondary" outline size="sm" onClick={reload} disabled={fuarLoading || globalLoading}>
                  <RefreshCw size={14} className="me-1" />{tx("common:yenile")}
                </Button>
                <Button color="success" size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus size={14} className="me-1" />{tx("firmaKartlar.yeniFirma")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ─── Sekme 1: Fuara eşlenmiş firmalar ─────────────────────────────── */}
        {activeTab === TAB_FUAR && (
          <>
            <p className="text-muted fs-12 mb-2 px-1">
              {tx("firmaKartlar.fuar")}{fuarId}{rolOzeti ? tx("firmaKartlar.buSayfada", { rolOzeti }) : ""}
            </p>

            <SectionState
              loading={fuarLoading}
              error={fuarError}
              empty={!fuarLoading && !fuarError && fuarRows.length === 0}
              emptyText={fuarSearch ? tx("firmaKartlar.ileEslesenFirmaBulunamadi", { search: fuarSearch }) : tx("firmaKartlar.buFuaraHenuzFirmaEslenmemis")}
            />

            {!fuarLoading && !fuarError && fuarRows.length > 0 && (
              <>
                {fuarRows.map((firma) => (
                  <FirmaKart key={firma.firmaId} firma={firma} variant="fuar" href={firmaUrl(firma)} onDetay={openFirma} />
                ))}
                <PagerRow
                  page={fuarPage}
                  pageCount={fuarPageCount}
                  total={fuarTotal}
                  onChange={setFuarPage}
                  label={tx("firmaKartlar.eslenmisFirma")}
                />
              </>
            )}
          </>
        )}

        {/* ─── Sekme 2: Tüm firma kütüğü ────────────────────────────────────── */}
        {activeTab === TAB_TUM && (
          <>
            <SectionState
              loading={globalLoading}
              error={globalError}
              empty={!globalLoading && !globalError && globalRows.length === 0}
              emptyText={search ? tx("firmaKartlar.ileEslesenFirmaBulunamadi", { search }) : tx("firmaKartlar.firmaKaydiBulunamadi")}
            />

            {!globalLoading && !globalError && globalRows.length > 0 && (
              <>
                {globalRows.map((firma) => (
                  <FirmaKart key={firma.firmaId} firma={firma} variant="global" href={firmaUrl(firma)} onDetay={openFirma} />
                ))}
                <PagerRow
                  page={globalPage}
                  pageCount={globalPageCount}
                  total={globalTotal}
                  onChange={setGlobalPage}
                  label={tx("firmaKartlar.firma")}
                />
              </>
            )}
          </>
        )}
      </Container>

      {/* Reset-on-open: key ile remount (bkz. PersonelIncelemePage.jsx aynı desen).
          forceLive — firmaAramaService.js modül seviyesinde mock'ta; bu sayfa canlı
          kütüğü gösterdiği için yaratılan firma da canlıya yazılmalı. */}
      <CreateFirmaModal
        key={createOpen ? "open" : "closed"}
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); reload(); }}
        fuarId={fuarId}
        forceLive
      />
    </div>
  );
}

/**
 * Sayfa numaraları — geçerli sayfanın etrafında en fazla 5 numara gösterir
 * (28.970 kayıt / 10 = 2.897 sayfa, hepsini basmak anlamsız).
 */
function PagerRow({ page, pageCount, total, onChange, label }) {
  const { t } = useTranslation("moduller");
  const pageNumbers = [];
  const start = Math.max(1, Math.min(page - 2, pageCount - 4));
  for (let i = start; i < start + 5 && i <= pageCount; i += 1) pageNumbers.push(i);

  return (
    // Standlar listesindeki gibi: satırların dışında, tam boy sayfa düğmeleri (WAS #593 öncesi: Pagination size="sm", kart içinde).
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
      <span className="text-muted small">
        {t("firmaKartlar.toplam")}{" "}{total.toLocaleString(currentLocaleTag())}{" "}{t("firmaKartlar.sayfa", { label, page })}{" "}{pageCount.toLocaleString(currentLocaleTag())}
      </span>
      {pageCount > 1 && (
        <Pagination className="mb-0">
          <PaginationItem disabled={page === 1}>
            <PaginationLink first onClick={() => onChange(1)} />
          </PaginationItem>
          <PaginationItem disabled={page === 1}>
            <PaginationLink previous onClick={() => onChange(page - 1)} />
          </PaginationItem>
          {pageNumbers.map((n) => (
            <PaginationItem key={n} active={n === page}>
              <PaginationLink onClick={() => onChange(n)}>{n}</PaginationLink>
            </PaginationItem>
          ))}
          <PaginationItem disabled={page >= pageCount}>
            <PaginationLink next onClick={() => onChange(page + 1)} />
          </PaginationItem>
          <PaginationItem disabled={page >= pageCount}>
            <PaginationLink last onClick={() => onChange(pageCount)} />
          </PaginationItem>
        </Pagination>
      )}
    </div>
  );
}
